import type { PendingComments } from '@/data/v2/pr/pending-comments-types';
import type { MergeabilityStatusResponse } from '@/lib/hooks/mergeability-status/query-data';
import type {
  PrV2CommentResponse,
  PrV2DiscussionResponse,
  PrV2ReviewResponse,
  PrV2ReviewThread,
  PrV2SummaryResponse,
} from '@/lib/pr-v2/types';

import type { PromotedPendingComment, ReviewTypeValue } from './review-submit-types';

import { recomputePrV2MergeabilityStatus } from '../mergeability-utils';
import { buildOptimisticThreadCommentNode } from './review-submit-optimistic';

import { MergeStateStatus } from '@/lib/github/graphql/types';
import { TIMELINE_ITEM_TYPE } from '@/lib/pr-v2/timeline-types';
import { asRecord } from '@/lib/pr-v2/utils/as-record';
import { ReviewType } from '@/types/github';

export function appendOptimisticComment(
  current: PrV2CommentResponse[] | undefined,
  optimisticComment: PrV2CommentResponse,
): PrV2CommentResponse[] {
  const existing = current ?? [];
  return [...existing, optimisticComment];
}

export function appendOptimisticTimelineItem(
  current: PrV2DiscussionResponse | undefined,
  optimisticTimelineItem: PrV2DiscussionResponse['timelineItems'][number],
): PrV2DiscussionResponse {
  const baseDiscussion: PrV2DiscussionResponse = current ?? {
    syncState: 'ready',
    timelineItems: [],
    commitCheckSuites: [],
    reactions: {},
    cachedAt: null,
  };

  return {
    ...baseDiscussion,
    timelineItems: [...baseDiscussion.timelineItems, optimisticTimelineItem],
  };
}

export function appendOptimisticThread(
  current: PrV2ReviewThread[] | undefined,
  optimisticThread: PrV2ReviewThread,
): PrV2ReviewThread[] {
  return [...(current ?? []), optimisticThread];
}

export function appendReplyToThread(
  current: PrV2ReviewThread[] | undefined,
  parentCommentId: string,
  replyComment: PrV2CommentResponse,
): PrV2ReviewThread[] | undefined {
  if (!current) {
    return current;
  }

  return current.map((thread) => {
    const isTargetThread = thread.comments.nodes.some((node) => {
      if (typeof node.databaseId === 'number' && parentCommentId === String(node.databaseId)) {
        return true;
      }

      return node.id === parentCommentId;
    });

    if (!isTargetThread) {
      return thread;
    }

    return {
      ...thread,
      comments: {
        ...thread.comments,
        nodes: [...thread.comments.nodes, buildOptimisticThreadCommentNode(replyComment)],
      },
    };
  });
}

export function removeOptimisticCommentsById(
  current: PrV2CommentResponse[] | undefined,
  optimisticCommentIds: string[],
): PrV2CommentResponse[] | undefined {
  if (!current || optimisticCommentIds.length === 0) {
    return current;
  }

  const idsToRemove = new Set(optimisticCommentIds);
  return current.filter((comment) => !idsToRemove.has(comment.githubCommentId));
}

export function removeReviewById(
  current: PrV2ReviewResponse[] | undefined,
  reviewId: string,
): PrV2ReviewResponse[] | undefined {
  if (!current) {
    return current;
  }

  return current.filter((review) => review.githubReviewId !== reviewId);
}

export function removeOptimisticTimelineItemsById(
  current: PrV2DiscussionResponse | undefined,
  optimisticCommentIds: string[],
): PrV2DiscussionResponse | undefined {
  if (!current || optimisticCommentIds.length === 0) {
    return current;
  }

  const idsToRemove = new Set(optimisticCommentIds);
  return {
    ...current,
    timelineItems: current.timelineItems.filter((item) => !idsToRemove.has(item.id)),
  };
}

export function buildOptimisticReplyParentIdRemap(
  pendingCommentsSnapshot: PendingComments[],
  promotedPendingComments: PromotedPendingComment[],
  idMapping: Record<string, number>,
): Map<string, string> {
  // Replies to pending comments initially point at optimistic parent ids and need remapping once
  // GitHub returns canonical ids for the promoted comments.
  const optimisticCommentIdByTempId = new Map(
    promotedPendingComments.map((pendingComment) => [
      pendingComment.tempId,
      pendingComment.optimisticCommentId,
    ]),
  );
  const remappedReplyParentIds = new Map<string, string>();

  for (const pendingComment of pendingCommentsSnapshot) {
    if (!('in_reply_to_id' in pendingComment) || !pendingComment.parent_is_pending) {
      continue;
    }

    const optimisticCommentId = optimisticCommentIdByTempId.get(pendingComment.id);
    const remappedParentId = idMapping[String(pendingComment.in_reply_to_id)];

    if (
      !optimisticCommentId ||
      remappedParentId == null ||
      !Number.isSafeInteger(remappedParentId) ||
      remappedParentId <= 0
    ) {
      continue;
    }

    remappedReplyParentIds.set(optimisticCommentId, String(remappedParentId));
  }

  return remappedReplyParentIds;
}

export function remapOptimisticReplyParentsInComments(
  current: PrV2CommentResponse[] | undefined,
  remappedReplyParentIds: Map<string, string>,
): PrV2CommentResponse[] | undefined {
  if (!current || remappedReplyParentIds.size === 0) {
    return current;
  }

  return current.map((comment) => {
    const remappedParentId = remappedReplyParentIds.get(comment.githubCommentId);
    if (!remappedParentId) {
      return comment;
    }

    return {
      ...comment,
      inReplyToId: remappedParentId,
    };
  });
}

export function remapOptimisticReplyParentsInDiscussion(
  current: PrV2DiscussionResponse | undefined,
  remappedReplyParentIds: Map<string, string>,
): PrV2DiscussionResponse | undefined {
  if (!current || remappedReplyParentIds.size === 0) {
    return current;
  }

  return {
    ...current,
    timelineItems: current.timelineItems.map((item) => {
      if (item.type !== TIMELINE_ITEM_TYPE.ReviewComment) {
        return item;
      }

      const remappedParentId = remappedReplyParentIds.get(item.id);
      if (!remappedParentId) {
        return item;
      }

      return {
        ...item,
        parentId: remappedParentId,
        in_reply_to_id: Number.parseInt(remappedParentId, 10),
      };
    }),
  };
}

export function removeOptimisticThreadNodesByCommentId(
  current: PrV2ReviewThread[] | undefined,
  optimisticCommentIds: string[],
): PrV2ReviewThread[] | undefined {
  if (!current || optimisticCommentIds.length === 0) {
    return current;
  }

  const idsToRemove = new Set(optimisticCommentIds);

  return current
    .map((thread) => ({
      ...thread,
      comments: {
        ...thread.comments,
        nodes: thread.comments.nodes.filter((node) => !idsToRemove.has(node.id)),
      },
    }))
    .filter((thread) => thread.comments.nodes.length > 0);
}

function requestedReviewersFromSummary(
  current: PrV2SummaryResponse | undefined,
): Array<{ login: string; avatar_url: string | null }> {
  return (
    current?.pullRequest.metadataSummary?.reviewers.map((reviewer) => ({
      login: reviewer.login,
      avatar_url: reviewer.avatarUrl,
    })) ?? []
  );
}

export function removeRequestedReviewerFromSummary(
  current: PrV2SummaryResponse | undefined,
  reviewerLogin: string,
): PrV2SummaryResponse | undefined {
  if (!current) {
    return current;
  }

  const metadataRecord = asRecord(current.pullRequest.metadata) ?? {};
  const pullRequestMetadata = asRecord(metadataRecord.pullRequest) ?? {};
  const requestedReviewers = requestedReviewersFromSummary(current).filter(
    (reviewer) => reviewer.login.toLowerCase() !== reviewerLogin.toLowerCase(),
  );

  return {
    ...current,
    pullRequest: {
      ...current.pullRequest,
      metadataSummary: {
        ...current.pullRequest.metadataSummary,
        reviewers: requestedReviewers.map((reviewer) => ({
          login: reviewer.login,
          avatarUrl: reviewer.avatar_url,
        })),
      },
      metadata: {
        ...metadataRecord,
        pullRequest: {
          ...pullRequestMetadata,
          requested_reviewers: requestedReviewers,
        },
      },
      counts: {
        ...current.pullRequest.counts,
        reviews: current.pullRequest.counts.reviews + 1,
        comments: current.pullRequest.counts.comments,
      },
    },
  };
}

export function getRequestedReviewerForLogin(
  current: PrV2SummaryResponse | undefined,
  reviewerLogin: string,
): { login: string; avatar_url: string | null } | null {
  if (!current) {
    return null;
  }

  const requestedReviewers = requestedReviewersFromSummary(current);

  for (const reviewer of requestedReviewers) {
    if (reviewer.login.toLowerCase() === reviewerLogin.toLowerCase()) {
      return reviewer;
    }
  }

  return null;
}

export function restoreRequestedReviewerInSummary(
  current: PrV2SummaryResponse | undefined,
  requestedReviewer: { login: string; avatar_url: string | null } | null,
): PrV2SummaryResponse | undefined {
  if (!current) {
    return current;
  }

  const metadataRecord = asRecord(current.pullRequest.metadata) ?? {};
  const pullRequestMetadata = asRecord(metadataRecord.pullRequest) ?? {};
  const requestedReviewers = requestedReviewersFromSummary(current);

  const nextRequestedReviewers = [...requestedReviewers];
  if (
    requestedReviewer &&
    !nextRequestedReviewers.some(
      (reviewer) => reviewer.login.toLowerCase() === requestedReviewer.login.toLowerCase(),
    )
  ) {
    nextRequestedReviewers.push(requestedReviewer);
  }

  return {
    ...current,
    pullRequest: {
      ...current.pullRequest,
      metadataSummary: {
        ...current.pullRequest.metadataSummary,
        reviewers: nextRequestedReviewers.map((reviewer) => ({
          login: reviewer.login,
          avatarUrl: reviewer.avatar_url,
        })),
      },
      metadata: {
        ...metadataRecord,
        pullRequest: {
          ...pullRequestMetadata,
          requested_reviewers: nextRequestedReviewers,
        },
      },
      counts: {
        ...current.pullRequest.counts,
        reviews: Math.max(0, current.pullRequest.counts.reviews - 1),
      },
    },
  };
}

export function applyReviewToMergeability(
  current: MergeabilityStatusResponse | undefined,
  reviewType: ReviewTypeValue,
): MergeabilityStatusResponse | undefined {
  // Optimistic approvals advance approval counts, but they do not clear existing blocking
  // change requests until the server responds with authoritative mergeability.
  return recomputePrV2MergeabilityStatus(current, (status) => {
    const approvalStatus = { ...status.approvalStatus };

    if (reviewType === ReviewType.APPROVE) {
      approvalStatus.currentApprovalCount += 1;
      approvalStatus.passed =
        approvalStatus.currentApprovalCount >= approvalStatus.requiredApprovalCount &&
        !approvalStatus.hasBlockingChangeRequests;
    } else if (reviewType === ReviewType.REQUEST_CHANGES) {
      approvalStatus.hasBlockingChangeRequests = true;
      approvalStatus.passed = false;
    }

    return {
      ...status,
      approvalStatus,
      // A BLOCKED verdict taken while approvals were missing may have been waiting on this one,
      // so it is unknown until the server refetches it. A BLOCKED taken with approvals already
      // met came from another rule, and stays until the server says otherwise.
      ...(approvalStatus.passed && !status.approvalStatus.passed
        ? { mergeStateStatus: MergeStateStatus.Unknown }
        : {}),
    };
  });
}
